import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { pushFinFoxMessage } from "@/lib/finfoxBus";

const SEEN_KEY = "finplay:finfox-notice-seen";
const LAST_CYCLE_KEY = "finplay:last-seen-league-cycle";

const wasSeen = (id: string) => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? (JSON.parse(raw) as string[]).includes(id) : false;
  } catch {
    return false;
  }
};

const markSeen = (id: string) => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (!list.includes(id)) list.push(id);
    localStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-20)));
  } catch {
    /* ignore */
  }
};

const daysBetween = (a: Date, b: Date) =>
  Math.floor((a.setHours(0, 0, 0, 0) - b.setHours(0, 0, 0, 0)) / 86_400_000);

/**
 * Welcome-back checks that feed the bottom-right FinFox assistant:
 * a lapsed learning streak and a fresh League cycle.
 * Renders nothing itself — FinFox is the single place messages appear.
 * Read-only: never touches portfolio, XP or streak data.
 */
export default function FinFoxNotices() {
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || cancelled) return;

      // 1. Streak lapse — read-only check on the user's own stats row.
      const { data: stats } = await supabase
        .from("tinder_user_stats")
        .select("current_streak, longest_streak, last_activity_date")
        .eq("user_id", user.id)
        .maybeSingle();

      if (stats?.last_activity_date) {
        const gap = daysBetween(new Date(), new Date(stats.last_activity_date));
        const previousStreak = stats.current_streak || stats.longest_streak || 0;
        if (gap >= 2 && previousStreak > 0) {
          const id = `streak:${stats.last_activity_date}`;
          if (!wasSeen(id) && !cancelled) {
            markSeen(id);
            pushFinFoxMessage({
              id,
              kind: "notice",
              mood: "curious",
              title: "Your streak paused",
              body: `Your ${previousStreak}-day streak stopped ${gap} days ago. One Fin-der card today starts it again — your XP and level are safe.`,
              cta: { label: "Swipe one card", href: "/financial-tinder" },
            });
          }
        }
      }

      // 2. League cycle reset — active cycle changed since the last visit.
      const { data: week } = await supabase
        .from("league_weeks")
        .select("id, start_date, end_date, month_number")
        .eq("is_active", true)
        .order("start_date", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (week?.id) {
        let lastSeenCycle: string | null = null;
        try {
          lastSeenCycle = localStorage.getItem(LAST_CYCLE_KEY);
        } catch {
          /* ignore */
        }

        if (lastSeenCycle && lastSeenCycle !== week.id) {
          const { data: previous } = await supabase
            .from("league_history")
            .select("final_rank, final_returns")
            .eq("user_id", user.id)
            .eq("week_id", lastSeenCycle)
            .maybeSingle();

          const id = `reset:${week.id}`;
          if (!wasSeen(id) && !cancelled) {
            const finish =
              previous?.final_rank != null
                ? ` You finished #${previous.final_rank} last cycle — it's saved in your history.`
                : "";
            markSeen(id);
            pushFinFoxMessage({
              id,
              kind: "notice",
              mood: "happy",
              title: "League standings reset",
              body: `A new League cycle started on ${new Date(week.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}, so the leaderboard begins fresh.${finish}`,
              cta: { label: "Open League", href: "/financial-league" },
            });
          }
        }

        try {
          localStorage.setItem(LAST_CYCLE_KEY, week.id);
        } catch {
          /* ignore */
        }
      }
    };

    // Delay slightly so it never competes with first paint.
    const t = setTimeout(run, 2500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, []);

  return null;
}
